/**
 * Skeleton Component - Loading placeholder
 * Professional loading states for better UX
 */

import type { HTMLAttributes } from 'react';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  width?: string;
  height?: string;
  variant?: 'text' | 'circular' | 'rectangular';
}

export function Skeleton({
  width = 'w-full',
  height = 'h-4',
  variant = 'rectangular',
  className = '',
  ...props
}: SkeletonProps) {
  const baseStyles = 'animate-pulse bg-neutral-200';

  const variantStyles = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  return (
    <div
      className={`${baseStyles} ${variantStyles[variant]} ${width} ${height} ${className}`}
      {...props}
    />
  );
}

/**
 * CardSkeleton - Pre-built skeleton for card components
 */
export function CardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-6">
      <div className="space-y-4">
        <Skeleton width="w-3/4" height="h-6" />
        <Skeleton width="w-1/2" height="h-10" />
        <div className="space-y-2">
          <Skeleton width="w-full" height="h-4" />
          <Skeleton width="w-5/6" height="h-4" />
        </div>
      </div>
    </div>
  );
}

/**
 * TableSkeleton - Pre-built skeleton for table rows
 */
export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-4">
          <Skeleton variant="circular" width="w-10" height="h-10" />
          <div className="flex-1 space-y-2">
            <Skeleton width="w-1/3" height="h-4" />
            <Skeleton width="w-1/2" height="h-3" />
          </div>
          <Skeleton width="w-20" height="h-8" />
        </div>
      ))}
    </div>
  );
}
